import { OperationVariables } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  IMetricHistoryConfig,
  IMetricHistoryExtraInput,
  useMetricHistory,
} from '../../../hooks/useMetricHistory';
import { IModalFilterContext } from '../../../types';
import { getPeriodLabel } from '../../../utils/getPeriodLabel';
import { GenericBarChart } from '../GenericBarChart';
import { HistoryChartContainer } from '../HistoryChartContainer';
import { StatusLegend } from '../StatusLegend';

interface IMetricHistoryChartProps<
  TData,
  TVariables extends OperationVariables,
  TPeriod extends { label: string },
  TLive
> {
  config: IMetricHistoryConfig<TData, TVariables, TPeriod, TLive>;
  live: TLive;
  filter: IModalFilterContext;
  extra: IMetricHistoryExtraInput;
  translationNs: string;
  isQuarterly?: boolean;
}

export function MetricHistoryChart<
  TData,
  TVariables extends OperationVariables,
  TPeriod extends { label: string },
  TLive
>({
  config,
  live,
  filter,
  extra,
  translationNs,
  isQuarterly,
}: IMetricHistoryChartProps<TData, TVariables, TPeriod, TLive>) {
  const { t } = useTranslation(translationNs);
  const { items, loading, periodType } = useMetricHistory(config, {
    live,
    filter,
    extra,
    t,
    isQuarterly,
  });

  return (
    <HistoryChartContainer
      loading={loading}
      empty={!loading && items.length === 0}
      title={t('historyTitle', { periodLabel: getPeriodLabel(periodType) })}
      historyDataCutoff={extra.historyDataCutoff}
    >
      <GenericBarChart items={items} valueSuffix="%" />
      <StatusLegend
        items={config.buildLegend(t, live, config.higherIsBetter)}
      />
    </HistoryChartContainer>
  );
}
